import React, {useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useQuery} from 'react-query';
import CustomersWrapper from './CustomersWrapper';
import CardSummary from 'components/CardSummary';
import {
  blueCheck,
  eye,
  greenCheckBaas,
  redCancel,
  roundIcon,
  corruptedFile,
} from 'assets/images/images';
import {
  FilterButton,
  PageLimit,
  Pagination,
  SearchInput,
  TableLayout,
  Loader,
  EmptyState,
  Filter,
} from 'components';
import {useSelector} from 'react-redux';
import {fetchBAASCards, fetchBAASCardsSummary} from 'requests/queries/baas';
import moment from 'moment';
import {filterOutEmptyValues, generateQueryParams} from 'helpers';
import AppliedFilters from 'components/AppliedFilters';

const initialFilters = {
  start: '',
  end: '',
};

function CustomerCards() {
  const {slug} = useParams();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [query, setQuery] = useState({
    page: 1,
    length: 10,
  });
  const [filters, setFilters] = useState(initialFilters);
  const [openFilter, setOpenFilter] = useState(false);
  const {userDetails} = useSelector(state => state?.auth);
  const [filterProps, setFilterProps] = useState(null);
  const [filtersApplied, setFiltersApplied] = useState(false);

  const formatDate = date => {
    if (!date) return '';
    return moment(date).format('YYYY-MM-DD');
  };

  const params = useMemo(
    () => ({
      company_id: userDetails && userDetails?.company?.id,
      ...query,
      search: searchText,
      customer_id: slug,
      start_date: formatDate(filters?.start),
      end_date: formatDate(filters?.end),
    }),
    [query, searchText, userDetails, filters, slug],
  );

  const {data: baas_cards, isLoading: loadingCards} = useQuery(
    ['baas-cards', {params}],
    () => fetchBAASCards({params}),
  );

  const summaryParams = useMemo(
    () => ({
      company_id: userDetails && userDetails?.company?.id,
      customer_id: slug,
      start_date: formatDate(filters?.start),
      end_date: formatDate(filters?.end),
    }),
    [userDetails, filters?.start, filters?.end, slug],
  );

  const {data: baas_cards_summary, isLoading: isLoadingSummary} = useQuery(
    ['baas-cards-summary', {summaryParams}],
    () => fetchBAASCardsSummary({params: summaryParams}),
  );

  const cards = useMemo(
    () => (!loadingCards && baas_cards?.entity?.cards) || [],
    [baas_cards, loadingCards],
  );

  const handlePageChange = page => {
    setQuery(prev => ({...prev, page}));
    const searchParams = new URLSearchParams(document.location.search);
    searchParams.set('page', page);
    const newSearchParams = searchParams.toString();
    navigate(`?${newSearchParams}`);
  };

  const handleResetFilter = () => {
    navigate('');
    setFilters(initialFilters);
    setOpenFilter(true);
    setSearchText('');
    setFiltersApplied(false);
  };

  const handleFilter = selectedOptions => {
    const filteredOptions = filterOutEmptyValues(selectedOptions);
    const queryParams = generateQueryParams(filteredOptions);
    let baseUrl = '';
    if (queryParams) {
      baseUrl += `?${queryParams}`;
    }

    setFilters(filteredOptions);
    setOpenFilter(false);
    navigate(baseUrl);
    setFiltersApplied(
      Object.values(filteredOptions).some(value => value) ||
        searchText.length > 0,
    );
  };

  const handleFilterStateChange = props => {
    setFilterProps(props);
    setFiltersApplied(
      Object.values(filters).some(value => value) || searchText.length > 0,
    );
  };

  const isNotFiltered = !loadingCards && (!cards || cards.length === 0);
  const isFiltered =
    isNotFiltered &&
    (Object.values(filters).some(s => s) ||
      (searchText && searchText.length > 0));

  const DefaultEmptyState = () => {
    if (isFiltered) {
      return (
        <EmptyState
          height={45}
          noBtn
          customBtn={
            <button
              onClick={() => {
                navigate('');
                setFilters(initialFilters);
                setSearchText('');
                setFiltersApplied(false);
                setOpenFilter(true);
              }}
              className="text-brandBlue p-4 text-sm font-medium"
            >
              Update preferences
            </button>
          }
          heading="No Cards Found"
          body="Oops, it looks like there are no cards that match the filters/search term you've selected. Please try adjusting your filters."
        />
      );
    }

    return (
      <EmptyState
        height={45}
        noBtn
        className="w-full"
        src={corruptedFile}
        heading="No Cards Found"
        body="Sorry, it appears there are no cards available for this customer."
        maxWidth="max-w-[500px]"
      />
    );
  };

  return (
    <CustomersWrapper>
      <div className="grid lg:grid-cols-4 gap-4 mt-8">
        <CardSummary
          className="!bg-white80"
          img={blueCheck}
          title="Total"
          body={baas_cards_summary?.entity?.total}
          loading={isLoadingSummary}
        />
        <CardSummary
          className="!bg-white80"
          img={greenCheckBaas}
          title="Active"
          body={baas_cards_summary?.entity?.active}
          loading={isLoadingSummary}
        />
        <CardSummary
          className="!bg-white80"
          img={redCancel}
          title="Frozen"
          body={baas_cards_summary?.entity?.frozzen}
          loading={isLoadingSummary}
        />
        <CardSummary
          className="!bg-white80"
          img={roundIcon}
          title="Terminated"
          body={baas_cards_summary?.entity?.terminated}
          loading={isLoadingSummary}
        />
      </div>
      <div className="flex items-center justify-between mt-6 flex-wrap gap-3">
        <div className="flex items-center gap-4 flex-wrap">
          <h3 className="text-grey text-[18px] font-medium">Cards</h3>
          {filtersApplied && filterProps && (
            <AppliedFilters {...filterProps} isMainPage={true} />
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <SearchInput onChange={value => setSearchText(value)} />
          <FilterButton openFilter={openFilter} setOpenFilter={setOpenFilter} />
        </div>
      </div>

      <Filter
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
        handleFilter={handleFilter}
        resetUrl={handleResetFilter}
        filterOptions={[]}
        onFilterStateChange={handleFilterStateChange}
      />

      <TableLayout negativeMargins className="w-full mt-7">
        {loadingCards ? (
          <Loader height={45} />
        ) : (
          <>
            <thead className="text-small font-semibold uppercase">
              <tr className="bg-white80">
                <td className="p-5 pl-12 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  card name
                </td>
                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  card number
                </td>
                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  card type
                </td>
                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  status
                </td>
                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  date/time
                </td>
                <td className="p-5 pl-0 "></td>
              </tr>
            </thead>
            <tbody className="text-[13px] text-body bg-white">
              {cards.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-4 text-center">
                    <DefaultEmptyState />
                  </td>
                </tr>
              ) : (
                cards.map(card => (
                  <tr
                    key={card.card_id}
                    className="text-left border-b bg-white border-grey60"
                  >
                    <td className="p-4 pl-12 whitespace-nowrap">{`${card.first_name} ${card.last_name}`}</td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      **** **** **** {card.last_four_digits}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      {card.card_type ? (
                        <span
                          className={`uppercase w-fit rounded-full px-2 py-1 h-5   flex items-center justify-center text-xs ${
                            card.card_type.toLowerCase() === 'visa'
                              ? 'bg-[#2A5392] text-white'
                              : 'bg-[#4A878D] text-white'
                          }`}
                        >
                          {card.card_type}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      <span
                        className={`uppercase w-fit rounded-full px-2 py-1 h-5   flex items-center justify-center  text-xs ${
                          card.status === 'Card - Active'
                            ? 'bg-[#F0F6EF] text-[#55A249]'
                            : card.status === 'Card - Frozen'
                            ? 'bg-[#F8EBEA] text-[#B42318]'
                            : 'bg-[#ECECEC] text-[#1D1D1D]'
                        }`}
                      >
                        {card.status === 'Card - Active'
                          ? 'Active'
                          : card.status === 'Card - Frozen'
                          ? 'Frozen'
                          : 'Terminated'}
                      </span>
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      {(card?.date_created &&
                        moment(card?.date_created).format('DD MMM YYYY h:m')) ||
                        '-'}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap flex items-center gap-4 justify-between">
                      <div className="flex items-center gap-5">
                        <button
                          onClick={() =>
                            navigate(
                              `/baas-customers/card-details/${card.card_id}`,
                            )
                          }
                        >
                          <img src={eye} alt="view" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </>
        )}
      </TableLayout>

      {!loadingCards && cards?.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4 mt-12 sm:gap-0">
          <PageLimit
            onLimitChange={length => {
              setQuery(prevState => ({
                ...prevState,
                length,
                page: 1,
              }));
            }}
            limit={query?.length}
            total={baas_cards?.entity?.total}
            length={cards?.length}
          />

          <Pagination
            total={baas_cards?.entity?.total_pages}
            current={Number(baas_cards?.entity?.current_page)}
            onPageChange={activePage => {
              handlePageChange(activePage);
            }}
          />
        </div>
      )}
    </CustomersWrapper>
  );
}

export default CustomerCards;
